/**
 * Payer Enrollment Group
 *
 * Payer-centric enrollment card: one card per payer, with each enrolled
 * transaction type (Claims / ERA) shown as a row with its own status and
 * provider-task form. This replaces the transaction-type-per-card model so the
 * UI matches how practices think ("get me set up to bill Payer X").
 */

import type { Enrollment } from '@features/enrollments';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { EnrollmentTaskForm } from './EnrollmentTaskForm';
import { getEnrollmentStatusConfig, getTransactionTypeLabel } from '../utils/enrollmentStatus';

interface PayerEnrollmentGroupProps {
  /** All enrollments for a single payer (one per transaction type). */
  enrollments: Enrollment[];
}

const TRANSACTION_ORDER: Record<string, number> = { '837P': 0, '835': 1 };

export function PayerEnrollmentGroup({ enrollments }: PayerEnrollmentGroupProps) {
  const theme = useTheme();

  if (enrollments.length === 0) {
    return null;
  }

  const [first] = enrollments;
  const payerName = first.payer_name || 'Unknown Payer';
  const rows = [...enrollments].sort(
    (a, b) =>
      (TRANSACTION_ORDER[a.transaction_type] ?? 9) -
      (TRANSACTION_ORDER[b.transaction_type] ?? 9),
  );

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        '&:hover': { borderColor: theme.palette.grey[300] },
      }}
    >
      {/* Payer header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, pb: 1.5 }}>
        <Avatar
          src={first.payer_avatar_url || undefined}
          alt={payerName}
          sx={{ width: 40, height: 40 }}
        >
          {payerName.charAt(0)}
        </Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
          {payerName}
        </Typography>
      </Box>

      {/* One row per transaction type */}
      {rows.map(enrollment => {
        const statusConfig = getEnrollmentStatusConfig(enrollment.status);
        const isLive = enrollment.status === 'LIVE';
        const liveDate = enrollment.live_at
          ? new Date(enrollment.live_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : null;

        return (
          <Box
            key={enrollment.stedi_transaction_enrollment_id}
            sx={{
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 64 }}>
                {getTransactionTypeLabel(enrollment.transaction_type)}
              </Typography>
              <Chip
                label={statusConfig.label}
                size="small"
                color={statusConfig.color}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isLive && liveDate && (
                  <Typography variant="caption" color="success.main">
                    Active since {liveDate}
                  </Typography>
                )}
                {enrollment.status === 'REJECTED' && enrollment.rejection_reason && (
                  <Typography variant="caption" color="error.main" noWrap>
                    {enrollment.rejection_reason}
                  </Typography>
                )}
              </Box>
              {enrollment.stedi_enrollment_id && (
                <Tooltip title="View in Stedi dashboard" arrow>
                  <IconButton
                    size="small"
                    onClick={() =>
                      window.open(
                        `https://portal.stedi.com/app/healthcare/enrollments/${enrollment.stedi_enrollment_id}`,
                        '_blank',
                      )
                    }
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Provider task completion (renders only when a task is open) */}
            <EnrollmentTaskForm enrollment={enrollment} />
          </Box>
        );
      })}
    </Box>
  );
}
